import React, { useEffect, useRef, useState } from "react";

const MAUVE = "#4c2f8e";
// Z plus haut pour être sûr qu'on le voie (contact peut rester à 100 si tu veux)
const Z = 110;
const NUDGE = -8;

// Dimensions identiques à l'autre badge (ultra discret)
const BADGE_INLINE = 14;   // épaisseur
const BADGE_BLOCK  = 120;  // hauteur
const BADGE_RADIUS = 12;   // arrondi

export default function SideInstallBadge() {
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const containerRef = useRef(null);
  const badgeRef = useRef(null);
  const [topPx, setTopPx] = useState(300); // position initiale (avant alignement)

  // Détection simple
  const ua = (typeof navigator !== "undefined" ? navigator.userAgent : "").toLowerCase();
  const isAndroid = /android/.test(ua);
  const isIOS = /iphone|ipad|ipod/.test(ua);

  // beforeinstallprompt
  useEffect(() => {
    const onBIP = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener("beforeinstallprompt", onBIP);
    return () => window.removeEventListener("beforeinstallprompt", onBIP);
  }, []);

  // Aligne sur le centre du badge de droite (#contact-badge)
  useEffect(() => {
    const contactSel = () => document.getElementById("contact-badge");

    const measure = () => {
      const contact = contactSel();
      const left = badgeRef.current;
      if (!contact || !left) return;

      const c = contact.getBoundingClientRect();
      const l = left.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;

      const contactCenter = c.top + scrollY + c.height / 2;
      const newTop = Math.round(contactCenter - l.height / 2 + NUDGE);
      setTopPx(newTop);
      // Force le GPU layer pour éviter les micro-décalages au 1er paint mobile
      if (containerRef.current) containerRef.current.style.transform = "translateZ(0)";
    };

    measure();
    const onScroll = () => measure();
    const onResize = () => measure();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    // observe le contact si sa taille/position change
    let ro;
    const c = contactSel();
    if (c && "ResizeObserver" in window) {
      ro = new ResizeObserver(measure);
      ro.observe(c);
    }

    // re-mesure à l’ouverture/fermeture (hauteur change)
    const mo = new MutationObserver(measure);
    if (containerRef.current) mo.observe(containerRef.current, { childList: true, subtree: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
      mo.disconnect();
    };
  }, [open]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } else {
      setOpen(true);
    }
  };

  // Instructions
  const Instructions = () => {
    if (isAndroid) {
      return (
        <ol style={{ marginLeft: 18, lineHeight: 1.5 }}>
          <li>Appuie sur <strong>Partager</strong> (carré avec flèche ⬆️) situé à côté de l'adresse padelmendyx.com dans la barre de recherche.</li>
          <li>Sélectionne <strong>Sur l’écran d’accueil</strong> en bas dans les propositions.</li>
          <li>Appuie sur <strong>Ajouter</strong>.</li>
          <li>L'application Padel Mendyx se trouve maintenant parmis <strong> les autres applications</strong> de votre téléphone.</li>
        </ol>
      );
    }
    if (isIOS) {
      return (
        <ol style={{ marginLeft: 18, lineHeight: 1.5 }}>
          <li>Appuie sur le bouton <strong>Partager</strong> (carré avec flèche ⬆️) situé à côté de l'adresse padelmendyx.com dans la barre de recherche</li>
          <li>Sélectionne <strong>Sur l’écran d’accueil</strong>  en bas dans les propositions.</li>
          <li>Appuie sur <strong>Ajouter</strong>.</li>
          <li>L'application Padel Mendyx se trouve maintenant parmis<strong> les applications</strong> sur votre téléphone.</li>

        </ol>
      );
    }
    return (
      <ol style={{ marginLeft: 18, lineHeight: 1.5 }}>
        <li>Clique sur l’<strong>icône d’installation</strong> dans la <strong>barre d’adresse</strong>.</li>
        <li>Valide pour installer l’application.</li>
      </ol>
    );
  };

  // Safe-area gauche iOS (évite d'être “sous” le bord)
  const paddingLeftSafeArea = (typeof window !== "undefined" && window.CSS && window.CSS.supports?.("padding-left: env(safe-area-inset-left)"))
    ? "env(safe-area-inset-left)" : 0;

  return (
    <div
      ref={containerRef}
      style={{
        position: "fixed",
        top: `${topPx}px`,
        left: 0,
        zIndex: Z,
        fontFamily: "sans-serif",
        paddingLeft: paddingLeftSafeArea,
        // couche GPU pour stabilité mobile
        willChange: "transform",
      }}
    >
      {/* Wrapper taille fixe : largeur = 12px → badge toujours visible */}
      <div style={{ position: "relative", width: BADGE_INLINE, height: BADGE_BLOCK }}>
        {/* Panneau coulissant (absolu, vers la droite) */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: BADGE_INLINE, // se place immédiatement à droite du badge
            width: 320,
            transform: open ? "translate(0, -50%)" : "translate(-100%, -50%)",
            transition: "transform .25s ease",
            background: MAUVE,
            color: "#fff",
            padding: "12px 20px",
            borderTopRightRadius: 12,
            borderBottomRightRadius: 12,
            boxShadow: open ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
            pointerEvents: open ? "auto" : "none",
            overflow: "hidden",
            willChange: "transform",
          }}
          role="region"
          aria-hidden={!open}
          aria-label="📲 Installer l’application"
        >
          <div style={{ fontWeight: 700, marginBottom: 8 }}>📲 Installer l’application</div>

          {deferredPrompt ? (
            <ol style={{ marginLeft: 18, lineHeight: 1.5 }}>
              <li>Appuie sur l’<strong>icône d’installation</strong> dans la barre d’adresse.</li>
              <li>Confirme avec <strong>Installer</strong>.</li>
            </ol>
          ) : (
            <Instructions />
          )}

          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
    
            <button
              onClick={() => setOpen(false)}
              style={{
                background: "transparent",
                color: "#fff",
                border: "1px solid rgba(255,255,255,.7)",
                borderRadius: 8,
                padding: "8px 12px",
                cursor: "pointer",
              }}
            >
              Fermer
            </button>
          </div>
        </div>

        {/* Badge vertical (boîte physique width/height — PAS de writing-mode ici) */}
        <button
          ref={badgeRef}
          onClick={() => setOpen((v) => !v)}
          id="install-badge"
          title="Installer l’application"
          aria-expanded={open}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: BADGE_INLINE,
            height: BADGE_BLOCK,
            backgroundColor: MAUVE,
            color: "#fff",
            border: 0,
            cursor: "pointer",
            // fermé : arrondi complet ; ouvert : côté droit plat pour coller au panneau
            borderRadius: open
              ? `${BADGE_RADIUS}px 0 0 ${BADGE_RADIUS}px`
              : `${BADGE_RADIUS}px`,
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            userSelect: "none",
            boxSizing: "border-box",
            touchAction: "manipulation",
          }}
        >
          {/* Texte vertical uniquement dans le span interne */}
          <span
            style={{
              writingMode: "vertical-rl",
              textOrientation: "mixed",
              fontWeight: 700,
              fontSize: "0.9rem",
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
          >
            {open ? "✖" : "Installer l’application"}
          </span>
        </button>
      </div>
    </div>
  );
}
